import { Big } from "big.js";

import type {
  HealthDataBillDiagnosis,
  HealthDataBillItem,
  HealthDataBillPriceTransparency,
  HealthDataBillTotals,
  HealthDataBillViewModel,
  HealthDataInvoiceItem,
  HealthDataInvoiceViewModel,
  Money,
} from "@medbillai/price-comparison";
import { v2 } from "@medbillai/schemas";

import { sentryService } from "../../../lib/sentry";
import { EobEnrichment } from "./healthDataEnrichment";
import type { BillTotals, NormalizedEob } from "./types";

/**
 * Bridge from the ingestion health-data read surface (`UserHealthRecord`) to the
 * bill screens' view-models. Parses the verbatim `data` JSON through `v2.EOB`
 * (claims) or `v2.Invoice` (invoices) and the `enrichment` overlay through the
 * FE-safe `EobEnrichment` mirror, then maps to the list shape (`NormalizedEob`)
 * and the detail shapes (`HealthDataBillViewModel` /
 * `HealthDataInvoiceViewModel`). A malformed `data` blob yields `null` instead
 * of throwing, so callers drop the row or render the empty state.
 */

/** A `UserHealthRecord` as Apollo delivers it: codegen types `data` and
 *  `enrichment` as `JSON` (string), but the live value is the parsed object. */
export type UserHealthRecordLike = {
  key: string;
  kind: string;
  data: unknown;
  enrichment?: unknown;
};

/** Maps `adjudication.decision` to the lowercase pill code the components use. */
export const decisionToPillCode = (
  decision: v2.Adjudication["decision"],
): string | undefined => {
  switch (decision) {
    case "APPROVED":
      return "approved";
    case "DENIED":
      return "denied";
    case "PARTIAL":
      return "partial";
    case "PENDING":
      return "pending";
    case "UNKNOWN":
      return undefined;
  }
};

/**
 * Maps `provider.networkStatus` to the lowercase pill code the components use.
 * `UNKNOWN` and any unmapped value resolve to `undefined` so the pill renders
 * "Unknown", never "In network".
 */
export const networkStatusToPillCode = (
  networkStatus: v2.NetworkStatus | undefined,
): string | undefined => {
  switch (networkStatus) {
    case "IN_NETWORK":
      return "in";
    case "OUT_OF_NETWORK":
      return "out";
    default:
      return undefined;
  }
};

const toMoney = (amount: number | undefined): Money | undefined =>
  amount === undefined ? undefined : { amount: Big(amount), currency: "USD" };

/** GraphQL `JSON` scalars are typed as `string` and may arrive either already
 *  parsed (object) or still encoded. Normalize before Zod so both shapes work. */
const coerceJsonValue = (value: unknown): unknown => {
  if (typeof value !== "string") {
    return value;
  }
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

export const parseEob = (data: unknown): v2.EOB | null => {
  const parsed = v2.EOB.safeParse(coerceJsonValue(data));
  if (!parsed.success) {
    console.error("Failed to parse v2.EOB record", parsed.error.message);
    return null;
  }
  return parsed.data;
};

const reportedParseFailures = new Set<string>();

// Issue paths and codes only: a Zod message can quote the record's values.
const reportParseFailure = (
  schema: string,
  key: string,
  issues: readonly { path: (string | number)[]; code: string }[],
) => {
  if (reportedParseFailures.has(`${schema}:${key}`)) {
    return;
  }
  reportedParseFailures.add(`${schema}:${key}`);
  sentryService.captureException(new Error(`Failed to parse ${schema}`), {
    tags: { feature: "bills-claims" },
    extra: {
      schema,
      key,
      issues: issues.map(issue => `${issue.path.join(".")}: ${issue.code}`),
    },
  });
};

export const parseStatement = (
  data: unknown,
  key: string,
): v2.Statement | null => {
  const parsed = v2.Statement.safeParse(coerceJsonValue(data));
  if (!parsed.success) {
    reportParseFailure("v2.Statement", key, parsed.error.issues);
    return null;
  }
  return parsed.data;
};

export const parseCollectionsNotice = (
  data: unknown,
  key: string,
): v2.CollectionsNotice | null => {
  const parsed = v2.CollectionsNotice.safeParse(coerceJsonValue(data));
  if (!parsed.success) {
    reportParseFailure("v2.CollectionsNotice", key, parsed.error.issues);
    return null;
  }
  return parsed.data;
};

export const parseInvoice = (data: unknown): v2.Invoice | null => {
  const parsed = v2.Invoice.safeParse(coerceJsonValue(data));
  if (!parsed.success) {
    console.error("Failed to parse v2.Invoice record", parsed.error.message);
    return null;
  }
  return parsed.data;
};

const parseEnrichment = (enrichment: unknown): EobEnrichment | null => {
  if (enrichment === null || enrichment === undefined) {
    return null;
  }
  const parsed = EobEnrichment.safeParse(coerceJsonValue(enrichment));
  return parsed.success ? parsed.data : null;
};

const deriveTitle = (eob: v2.EOB): string | undefined =>
  eob.provider.name || eob.facility?.name || undefined;

/**
 * Concise, service-focused title from the resolved procedure codes: the first
 * code's description, plus ` +N more` when more codes resolved. Decoupled from
 * the pre-joined `enrichment.title` (`<service> - <provider> - <date>`) so the
 * list and detail header don't repeat the provider and date they already render
 * in their own slots. Returns `undefined` when no code resolved, so callers fall
 * back to the provider name.
 */
const deriveServiceLabel = (
  codeDescriptions: EobEnrichment["codeDescriptions"],
): string | undefined => {
  const labels = Object.values(codeDescriptions ?? {})
    .map(entry => entry.display ?? entry.longText)
    .filter((label): label is string => Boolean(label));
  // Dedupe so two codes that resolve to the same label don't inflate `+N more`.
  const [first, ...rest] = [...new Set(labels)];
  if (!first) {
    return undefined;
  }
  return rest.length > 0 ? `${first} +${rest.length} more` : first;
};

const joinItemDescriptions = (eob: v2.EOB): string => {
  const description = eob.adjudication.items
    .map(item => item.description)
    .filter(Boolean)
    .join(", ");
  return description || "No summary available";
};

const joinInvoiceItemDescriptions = (invoice: v2.Invoice): string => {
  const description = invoice.items
    .map(item => item.description)
    .filter(Boolean)
    .join(", ");
  return description || "No summary available";
};

/** The list filter tabs (denied / in-network / out-of-network). The v1 path
 *  forwards these to the server; the health-data surface returns the full set,
 *  so we apply them client-side over the parsed records. */
export type HealthDataListFilter = {
  inNetwork?: boolean;
  outOfNetwork?: boolean;
  denied?: boolean;
};

/** Whether a parsed EOB satisfies the active filter tab. Unset flags don't
 *  constrain. The tabs are mutually exclusive in the UI. */
export const eobMatchesFilter = (
  eob: v2.EOB,
  filter: HealthDataListFilter,
): boolean => {
  if (filter.denied && eob.adjudication.decision !== "DENIED") {
    return false;
  }
  if (filter.inNetwork && eob.provider.networkStatus !== "IN_NETWORK") {
    return false;
  }
  if (filter.outOfNetwork && eob.provider.networkStatus !== "OUT_OF_NETWORK") {
    return false;
  }
  return true;
};

const mapEobToNormalized = (
  key: string,
  eob: v2.EOB,
  enrichment: EobEnrichment | null,
): NormalizedEob => {
  const serviceDate = eob.claim.serviceStartDate;
  return {
    id: key,
    resourceId: key,
    name: eob.patient.name || null,
    status: eob.claim.claimStatus,
    decision: decisionToPillCode(eob.adjudication.decision) ?? null,
    totalCost: toMoney(eob.adjudication.totals.patientResponsibility),
    location: eob.provider.name || eob.facility?.name || undefined,
    title: deriveServiceLabel(enrichment?.codeDescriptions) ?? deriveTitle(eob),
    serviceDate: serviceDate ? new Date(serviceDate) : undefined,
    description: joinItemDescriptions(eob),
    kind: "claim",
    // The health-data surface has no findings yet. The v1 findings path is
    // unchanged.
  };
};

/** The list row plus the parsed claim it came from, parsed once. */
export const normalizeClaimRecord = (
  record: UserHealthRecordLike,
): { row: NormalizedEob; eob: v2.EOB } | null => {
  const eob = parseEob(record.data);
  return eob
    ? {
        row: mapEobToNormalized(
          record.key,
          eob,
          parseEnrichment(record.enrichment),
        ),
        eob,
      }
    : null;
};

/** Map one health-data EOB record to the list `NormalizedEob`. Returns `null`
 *  for an unparseable `data` blob (caller filters it out). */
export const normalizeHealthDataEob = (
  record: UserHealthRecordLike,
): NormalizedEob | null => normalizeClaimRecord(record)?.row ?? null;

/** Map the full health-data EOB query result to the list shape, dropping
 *  unparseable rows and applying the active filter tab client-side. */
export const normalizeHealthDataEobs = (
  records: readonly UserHealthRecordLike[] | null | undefined,
  filter?: HealthDataListFilter,
): NormalizedEob[] => {
  if (!records) {
    return [];
  }
  const result: NormalizedEob[] = [];
  for (const record of records) {
    const eob = parseEob(record.data);
    if (!eob) {
      continue;
    }
    if (filter && !eobMatchesFilter(eob, filter)) {
      continue;
    }
    result.push(
      mapEobToNormalized(record.key, eob, parseEnrichment(record.enrichment)),
    );
  }
  return result;
};

const invoiceLocation = (invoice: v2.Invoice): string | undefined =>
  invoice.provider.name || invoice.issuer.name || undefined;

const invoiceServiceDate = (invoice: v2.Invoice): string =>
  invoice.meta.serviceStartDate || invoice.meta.invoiceDate;

// Amount still owed. When balance is absent, fall back to net minus payments
// (not bare net) so this matches the cost breakdown's "you still owe".
// Undefined, not 0, when neither is printed.
export const invoiceBalance = (invoice: v2.Invoice): number | undefined =>
  invoice.totals.balance ??
  (invoice.totals.net != null
    ? invoice.totals.net - (invoice.totals.paidSoFar ?? 0)
    : undefined);

const mapInvoiceToNormalized = (
  key: string,
  invoice: v2.Invoice,
  enrichment: EobEnrichment | null,
): NormalizedEob => {
  const serviceDate = invoiceServiceDate(invoice);
  return {
    id: key,
    resourceId: key,
    name: invoice.patient.name || null,
    status: invoice.meta.status,
    // Invoices carry no adjudication decision or network status, so they appear
    // only under the "All" tab.
    decision: null,
    totalCost: toMoney(invoiceBalance(invoice)),
    location: invoiceLocation(invoice),
    title: enrichment?.title ?? invoice.meta.title ?? invoiceLocation(invoice),
    serviceDate: serviceDate ? new Date(serviceDate) : undefined,
    description: joinInvoiceItemDescriptions(invoice),
    kind: "invoice",
  };
};

/** The list row plus the parsed invoice it came from, parsed once. */
export const normalizeInvoiceRecord = (
  record: UserHealthRecordLike,
): { row: NormalizedEob; invoice: v2.Invoice } | null => {
  const invoice = parseInvoice(record.data);
  return invoice
    ? {
        row: mapInvoiceToNormalized(
          record.key,
          invoice,
          parseEnrichment(record.enrichment),
        ),
        invoice,
      }
    : null;
};

/** Map the full health-data invoice query result to the list shape, dropping
 *  unparseable rows. Invoices have no decision/network, so the list filter
 *  tabs never constrain them (the caller shows invoices under "All" only). */
export const normalizeHealthDataInvoices = (
  records: readonly UserHealthRecordLike[] | null | undefined,
): NormalizedEob[] => {
  if (!records) {
    return [];
  }
  const result: NormalizedEob[] = [];
  for (const record of records) {
    const invoice = parseInvoice(record.data);
    if (!invoice) {
      continue;
    }
    result.push(
      mapInvoiceToNormalized(
        record.key,
        invoice,
        parseEnrichment(record.enrichment),
      ),
    );
  }
  return result;
};

const toBillTotals = (totals: v2.AdjudicationTotals): HealthDataBillTotals => ({
  submitted: toMoney(totals.billedAmount),
  benefit: toMoney(totals.benefit),
  discount: toMoney(totals.discount),
  memberLiability: toMoney(totals.patientResponsibility),
  paidByPatient: toMoney(totals.paidByPatient),
});

const toInvoiceTotals = (totals: v2.InvoiceTotals): HealthDataBillTotals => ({
  submitted: toMoney(totals.gross ?? undefined),
  discount: toMoney(totals.discount ?? undefined),
  // Pre-payment share. BillCostInfo subtracts paidByPatient to show "you still
  // owe", so this must be net (not balance) for that to equal the balance.
  memberLiability: toMoney(totals.net ?? undefined),
  paidByPatient: toMoney(totals.paidSoFar ?? undefined),
});

/**
 * Project a health-data view-model's totals onto the shared `BillTotals` shape
 * the cost rows and dispute dialog read. The keys are a subset of `BillTotals`'s,
 * so the cost components stay source-agnostic.
 */
export const billViewModelTotalsToBillTotals = (
  totals: HealthDataBillTotals,
): BillTotals => {
  const result: BillTotals = {};
  if (totals.submitted) {
    result.submitted = totals.submitted;
  }
  if (totals.benefit) {
    result.benefit = totals.benefit;
  }
  if (totals.discount) {
    result.discount = totals.discount;
  }
  if (totals.memberLiability) {
    result.memberLiability = totals.memberLiability;
  }
  if (totals.paidByPatient) {
    result.paidByPatient = totals.paidByPatient;
  }
  return result;
};

const toBillDiagnosis = (diagnosis: v2.Diagnosis): HealthDataBillDiagnosis => ({
  code: diagnosis.code || undefined,
  description: diagnosis.description ?? diagnosis.display ?? undefined,
});

const toBillItem = (
  item: v2.AdjudicationItem,
  codeDescriptions: EobEnrichment["codeDescriptions"],
): HealthDataBillItem => {
  // Prefer the catalog-resolved description for the line's procedure code over
  // the raw source text (often a generic "MISCELLANEOUS SERVICES"). Falls back
  // to the source text when the code is absent or unresolved.
  const resolved = item.procedureCode
    ? codeDescriptions?.[item.procedureCode]
    : undefined;
  return {
    sequence: item.sequence,
    description: resolved?.display ?? resolved?.longText ?? item.description,
    procedureCode: item.procedureCode,
    procedureCodeType: item.procedureCodeType,
    isDenied: item.isDenied ?? false,
    denialReasons: (item.denialReasons ?? []).map(reason => ({
      code: reason.code,
      description: reason.description,
    })),
    diagnoses: (item.diagnoses ?? []).map(toBillDiagnosis),
  };
};

/**
 * Flatten and dedupe per-line diagnoses up to the claim level. The health-data
 * shape has no claim-level diagnosis array (the v1 FHIR path read `eob.diagnosis[]`). Dedupe on
 * code, falling back to description when a code is absent.
 */
const collectDiagnoses = (eob: v2.EOB): HealthDataBillDiagnosis[] => {
  const seen = new Set<string>();
  const result: HealthDataBillDiagnosis[] = [];
  for (const item of eob.adjudication.items) {
    for (const diagnosis of item.diagnoses ?? []) {
      const mapped = toBillDiagnosis(diagnosis);
      const dedupeKey = mapped.code ?? mapped.description ?? "";
      if (dedupeKey && !seen.has(dedupeKey)) {
        seen.add(dedupeKey);
        result.push(mapped);
      }
    }
  }
  return result;
};

/** Project `enrichment.priceTransparency` into the detail price block. Empty
 *  when enrichment is absent, in which case the detail view hides the block. */
const collectPriceTransparency = (
  enrichment: EobEnrichment | null,
): HealthDataBillPriceTransparency[] => {
  const priceTransparency = enrichment?.priceTransparency;
  if (!priceTransparency) {
    return [];
  }
  return Object.entries(priceTransparency).map(([procedureCode, entry]) => ({
    procedureCode,
    median: { amount: Big(entry.median), currency: "USD" },
    rating: entry.rating,
  }));
};

const PRICE_RATING_HIGH = "HIGH";

/** High-price signal: the enrichment overall rating is HIGH, or any code's
 *  price rating is HIGH. Never true without price data. */
const deriveIsHighPrice = (
  enrichment: EobEnrichment | null,
  priceTransparency: HealthDataBillPriceTransparency[],
): boolean => {
  if (priceTransparency.length === 0) {
    return false;
  }
  if (enrichment?.overallPriceRating === PRICE_RATING_HIGH) {
    return true;
  }
  return priceTransparency.some(entry => entry.rating === PRICE_RATING_HIGH);
};

/** Build the bill-detail view-model from a health-data EOB record. Returns
 *  `null` for an unparseable `data` blob (caller renders the not-found state). */
export const buildBillViewModel = (
  record: UserHealthRecordLike,
): HealthDataBillViewModel | null => {
  const eob = parseEob(record.data);
  if (!eob) {
    return null;
  }
  const enrichment = parseEnrichment(record.enrichment);
  const priceTransparency = collectPriceTransparency(enrichment);

  return {
    key: record.key,
    title: deriveServiceLabel(enrichment?.codeDescriptions) ?? deriveTitle(eob),
    patientName: eob.patient.name || undefined,
    providerName: eob.provider.name || undefined,
    facilityName: eob.facility?.name || undefined,
    serviceDate: eob.claim.serviceStartDate || undefined,
    decisionCode: decisionToPillCode(eob.adjudication.decision),
    networkCode: networkStatusToPillCode(eob.provider.networkStatus),
    patientResponsibility: toMoney(
      eob.adjudication.totals.patientResponsibility,
    ),
    totals: toBillTotals(eob.adjudication.totals),
    items: eob.adjudication.items.map(item =>
      toBillItem(item, enrichment?.codeDescriptions),
    ),
    diagnoses: collectDiagnoses(eob),
    priceTransparency,
    overallPriceRating: enrichment?.overallPriceRating ?? undefined,
    isHighPrice: deriveIsHighPrice(enrichment, priceTransparency),
  };
};

const toInvoiceItem = (item: v2.InvoiceItem): HealthDataInvoiceItem => ({
  sequence: item.sequence,
  description: item.description,
  amount: { amount: Big(item.amount), currency: "USD" },
});

/** Build the invoice-detail view-model from a health-data invoice record.
 *  Returns `null` for an unparseable `data` blob (caller renders the not-found
 *  state). Invoices have no adjudication, so the view-model omits
 *  decision/network/diagnosis. */
export const buildInvoiceViewModel = (
  record: UserHealthRecordLike,
): HealthDataInvoiceViewModel | null => {
  const invoice = parseInvoice(record.data);
  if (!invoice) {
    return null;
  }
  const enrichment = parseEnrichment(record.enrichment);

  return {
    key: record.key,
    title: enrichment?.title ?? invoice.meta.title ?? invoiceLocation(invoice),
    patientName: invoice.patient.name || undefined,
    providerName: invoiceLocation(invoice),
    serviceDate: invoiceServiceDate(invoice) || undefined,
    amountDue: toMoney(invoiceBalance(invoice)),
    totals: toInvoiceTotals(invoice.totals),
    items: invoice.items.map(toInvoiceItem),
  };
};
